/**
 * Registering this browser for push, in one place.
 *
 * Lifted out of use-push-subscription so the account-level prompt and the
 * queue-level auto-subscribe run identical code. They must: a subscription
 * registered one way and a subscription registered the other are the same
 * record on the server, and two implementations of "subscribe" that drift
 * apart produce notifications that work in one part of the app and not the
 * other, which is close to impossible to diagnose from a bug report.
 */

const BACKEND_URL = (() => {
  const raw =
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_BACKEND_URL_DEV ||
    'http://localhost:4200';
  return raw.replace(/\/api\/v1\/?$/, '');
})();

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export const pushSupported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * Whether this browser, on this origin, currently holds a push subscription.
 *
 * Origin matters and is the entire reason the prompt exists. A subscription
 * belongs to the service worker registration that created it, so the answer
 * here is about this site on this device — never about the account, which may
 * well have working subscriptions on other devices or, after the domain move,
 * on the origin Standby used to live at.
 */
export async function hasLocalPushSubscription(): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!registration) return false;
    return (await registration.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

interface SubscribeOptions {
  participantId?: number | null;
  participantToken?: string | null;
}

/**
 * Subscribes this browser and registers it with the API.
 *
 * Resolves false rather than throwing for every ordinary way this ends without
 * a subscription — permission denied, no VAPID key, no support. Callers treat
 * those the same way, and the queue path in particular must not have its setup
 * interrupted because a browser said no.
 */
export async function ensurePushSubscription(
  opts: SubscribeOptions = {}
): Promise<boolean> {
  if (!pushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    let vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/v1/push/vapid-key`);
        vapidKey = (await res.json()).publicKey;
      } catch {
        return false;
      }
    }
    if (!vapidKey) return false;

    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      }));

    const p256dh = subscription.getKey('p256dh');
    const auth = subscription.getKey('auth');
    if (!p256dh || !auth) return false;

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const authToken = localStorage.getItem('standby_token');
    if (authToken) headers.Authorization = `Bearer ${authToken}`;

    await fetch(`${BACKEND_URL}/api/v1/push/subscribe`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        p256dh: arrayBufferToBase64(p256dh),
        auth: arrayBufferToBase64(auth),
        ...(opts.participantId
          ? {
              participantId: opts.participantId,
              participantToken: opts.participantToken || undefined
            }
          : {})
      })
    });

    return true;
  } catch (err) {
    console.warn('Push subscription failed:', err);
    return false;
  }
}

/**
 * Whether the account expects notifications — asked of the server, because it
 * is the only party that can see past this browser. Answers false for anyone
 * signed out or on any error: a prompt shown to someone who never wanted
 * notifications is worse than one not shown at all.
 */
export async function accountExpectsPush(): Promise<boolean> {
  const authToken = localStorage.getItem('standby_token');
  if (!authToken) return false;
  try {
    const res = await fetch(`${BACKEND_URL}/api/v1/push/expectation`, {
      headers: { Authorization: `Bearer ${authToken}` },
      credentials: 'include'
    });
    if (!res.ok) return false;
    return (await res.json())?.expectsPush === true;
  } catch {
    return false;
  }
}
